import React, { useState, useCallback, useRef, useEffect } from "react";
import { Avatar } from "../../../../ui/components/Avatar";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { Table } from "../../../../ui/components/Table";
import { TextField } from "../../../../ui/components/TextField";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { FeatherCheck } from "@subframe/core";
import { FeatherSearch } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { useNavigate, useLocation } from "react-router-dom";
import {
  searchVendors,
  VendorSearchItem,
} from "../../../../services/vendorApi";
import { handleError, AppError } from "../../../../utils/errorHandling";
import ErrorDisplay from "../../../../ui/components/ErrorDisplay";
import { BillDataEntity } from "../../domain/entities/BillEntity";
import { CREATE_MANUAL_BILL_RETURN_TO } from "../utils/manualBillVendorPatch";

interface SearchPartyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPartySelect: (party: any) => void;
  id?: string;
  initialSearchTerm?: string;
  initialSelectedVendorId?: string;
  /** When set, Add/Edit party navigates back with bill draft + optional vendor patch (no sessionStorage). */
  createManualBillFlow?: {
    returnPath: string;
    billDraft: BillDataEntity;
  };
}

const SearchPartyModal: React.FC<SearchPartyModalProps> = ({
  open,
  onOpenChange,
  onPartySelect,
  id,
  initialSearchTerm,
  initialSelectedVendorId,
  createManualBillFlow,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedParty, setSelectedParty] = useState<VendorSearchItem | null>(
    null,
  );
  const [hasSearched, setHasSearched] = useState(false);
  const [isInitialSearch, setIsInitialSearch] = useState(false);
  const [vendors, setVendors] = useState<VendorSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const idsMatch = useCallback(
    (left?: string | number | null, right?: string | number | null) => {
      if (
        left === undefined ||
        left === null ||
        right === undefined ||
        right === null
      ) {
        return false;
      }
      return String(left).trim() === String(right).trim();
    },
    [],
  );

  const isVendorSelected = useCallback(
    (vendor: { id: string; zoho_vendorid: string }) => {
      if (!selectedParty) return false;
      return (
        idsMatch(selectedParty.vendorid, vendor.id) ||
        idsMatch(selectedParty.zoho_vendorid, vendor.zoho_vendorid)
      );
    },
    [idsMatch, selectedParty],
  );

  const performSearch = useCallback(async (term: string) => {
    const trimmedSearch = term.trim();
    if (!trimmedSearch || trimmedSearch.length < 2) return;

    setLoading(true);
    setError(null);
    try {
      const response = await searchVendors({
        search: trimmedSearch,
        page: 1,
        limit: 10,
        status: "active",
      });
      setVendors(response.data || []);
      setHasSearched(true);
      setIsInitialSearch(false); // Initial search completed, allow typing
    } catch (err) {
      setError(handleError(err, "vendor search"));
      setVendors([]);
      setIsInitialSearch(false); // Even on error, allow typing
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle modal reopen after add/edit (keeps modal open and refreshes search)
  useEffect(() => {
    if (location.state?.reopenSearchModal && location.state?.searchTerm) {
      // Reopen the modal
      onOpenChange(true);
      setSearchTerm(location.state.searchTerm);
      setIsInitialSearch(true); // Mark as initial search
      performSearch(location.state.searchTerm);
      // Clear the state
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, navigate, performSearch, onOpenChange]);

  // Auto-search when modal opens; prefer searching by ID if already selected in bill JSON
  useEffect(() => {
    const runInitialSearch = async () => {
      if (!open) {
        return;
      }

      const selectedVendorId = initialSelectedVendorId?.trim();
      const trimmedInitialTerm = initialSearchTerm?.trim();

      if (selectedVendorId) {
        setSearchTerm(initialSearchTerm || "");
        setIsInitialSearch(true);
        setLoading(true);
        setError(null);
        try {
          const response = await searchVendors({
            vendor_id: selectedVendorId,
            status: "active",
            page: 1,
            limit: 10,
          });
          setVendors(response.data || []);
          setHasSearched(true);
        } catch (err) {
          setError(handleError(err, "vendor search"));
          setVendors([]);
        } finally {
          setLoading(false);
          setIsInitialSearch(false);
        }
        return;
      }

      if (trimmedInitialTerm) {
        setSearchTerm(initialSearchTerm || "");
        setIsInitialSearch(true);
        await performSearch(trimmedInitialTerm);
      }
    };

    runInitialSearch();
  }, [initialSearchTerm, initialSelectedVendorId, open, performSearch]);

  useEffect(() => {
    if (initialSelectedVendorId && vendors.length > 0 && !selectedParty) {
      const vendorToSelect = vendors.find(
        (vendor) =>
          idsMatch(vendor.vendorid, initialSelectedVendorId) ||
          idsMatch(vendor.zoho_vendorid, initialSelectedVendorId),
      );
      if (vendorToSelect) {
        setSelectedParty(vendorToSelect);
      }
    }
  }, [idsMatch, initialSelectedVendorId, selectedParty, vendors]);

  const handleClose = () => {
    onOpenChange(false);
    setSearchTerm("");
    setSelectedParty(null);
    setHasSearched(false);
    setIsInitialSearch(false);
    setVendors([]);
    setError(null);
  };

  const handlePartySelect = (party: VendorSearchItem) => {
    setSelectedParty(party);
  };

  const handleConfirm = () => {
    if (selectedParty) {
      // Pass vendor data along with party selection
      onPartySelect({
        ...selectedParty,
        name: selectedParty.vendor_legal_name,
        zoho_vendorid: selectedParty.zoho_vendorid,
        address: selectedParty.registered_office_address,
        gst_treatment: selectedParty.gst_treatment,
        gst_category: selectedParty.gst_category,
        status: selectedParty.status === "active" ? "Verified" : "Pending",
      });
      handleClose();
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback((searchValue: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (searchValue.trim()) {
        setLoading(true);
        setError(null);
        try {
          const response = await searchVendors({
            search: searchValue.trim(),
            page: 1,
            limit: 10,
            status: "active",
          });
          setVendors(response.data || []);
          setHasSearched(true);
          setIsInitialSearch(false); // Allow typing after search completes
        } catch (err) {
          setError(handleError(err, "vendor search"));
          setVendors([]);
          setIsInitialSearch(false); // Even on error, allow typing
        } finally {
          setLoading(false);
        }
      } else {
        setVendors([]);
        setHasSearched(false);
        setSelectedParty(null);
      }
    }, 300); // 300ms debounce
  }, []);

  const handleSearch = async () => {
    const trimmedSearch = searchTerm.trim();

    if (!trimmedSearch) {
      setError(
        handleError(
          new Error("Please enter a search term to find vendors."),
          "validation",
        ),
      );
      return;
    }

    if (trimmedSearch.length < 2) {
      setError(
        handleError(
          new Error(
            "Please enter at least 2 characters to search for vendors.",
          ),
          "validation",
        ),
      );
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await searchVendors({
        search: trimmedSearch,
        page: 1,
        limit: 10,
        status: "active",
      });
      setVendors(response.data || []);
      setHasSearched(true);
      setIsInitialSearch(false); // Allow typing after search completes
    } catch (err) {
      setError(handleError(err, "vendor search"));
      setVendors([]);
      setIsInitialSearch(false); // Even on error, allow typing
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setSearchTerm(value);

    // Clear error when user starts typing again
    if (error) {
      setError(null);
    }

    debouncedSearch(value);
  };

  // Convert vendors to display format
  const displayVendors = (vendors || []).map((vendor) => ({
    id: vendor.vendorid,
    zoho_vendorid: vendor.zoho_vendorid,
    name: vendor.vendor_legal_name,
    tradingName: vendor.trading_name,
    taxId: vendor.gstin,
    address: vendor.registered_office_address || "Address not available",
    status: vendor.status === "active" ? "Verified" : "Pending",
    riskRating: vendor.risk_rating,
    businessCategory: vendor.business_category,
    type: vendor.type_of_vendor,
    email: vendor.primary_contact_email,
    avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(
      vendor.vendor_legal_name,
    )}&background=random`,
  }));

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-320 flex-col items-start gap-6 bg-default-background px-8 py-6">
        <div className="flex w-full items-center justify-between">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Search Party
          </span>
          <IconButton icon={<FeatherX />} onClick={handleClose} />
        </div>
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-96">
            <span className="text-body-bold font-body-bold text-default-font">
              Search Existing Party
            </span>
          </div>
        </div>
        <div className="flex w-full flex-col items-start gap-4">
          <div className="flex w-full items-center gap-4">
            <TextField
              className="grow"
              variant="filled"
              label=""
              helpText=""
              icon={<FeatherSearch />}
            >
              <TextField.Input
                placeholder="Search by name, tax ID, or address..."
                value={searchTerm}
                onChange={handleSearchInputChange}
                disabled={loading && isInitialSearch}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !(loading && isInitialSearch)) {
                    handleSearch();
                  }
                }}
              />
            </TextField>
            <Button
              variant="brand-primary"
              icon={<FeatherSearch />}
              onClick={handleSearch}
              disabled={!searchTerm.trim() || loading}
              loading={loading}
            >
              Search
            </Button>
            {/* <Button
              variant="neutral-tertiary"
              iconRight={<FeatherChevronDown />}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
            >
              Filters
            </Button> */}
          </div>
          {loading ? (
            <div className="flex w-full items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-brand-primary"></div>
                </div>
                <span className="text-body font-body text-neutral-500">
                  Searching vendors...
                </span>
              </div>
            </div>
          ) : error ? (
            <ErrorDisplay
              error={error}
              onRetry={handleSearch}
              onLogin={() => (window.location.href = "/login")}
            />
          ) : !hasSearched ? (
            <div className="flex w-full items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100">
                  <FeatherSearch className="h-8 w-8 text-neutral-400" />
                </div>
                <span className="text-body font-body text-neutral-500">
                  Enter a search term to find vendors
                </span>
              </div>
            </div>
          ) : displayVendors.length === 0 ? (
            <div className="flex w-full items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-neutral-100">
                  <FeatherSearch className="h-8 w-8 text-neutral-400" />
                </div>
                <span className="text-body font-body text-neutral-500">
                  No vendors found for "{searchTerm}"
                </span>
              </div>
            </div>
          ) : (
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell className="h-8 w-96 flex-none">
                    Vendor Name
                  </Table.HeaderCell>
                  <Table.HeaderCell className="h-8 w-40 flex-none">
                    GSTIN
                  </Table.HeaderCell>
                  <Table.HeaderCell className="h-8 w-32 flex-none">
                    Business Category
                  </Table.HeaderCell>
                  <Table.HeaderCell className="h-8 w-24 flex-none">
                    Status
                  </Table.HeaderCell>
                  <Table.HeaderCell className="h-8 w-40 flex-none">
                    Actions
                  </Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {displayVendors.map((vendor) => (
                <Table.Row key={vendor.id}>
                  <Table.Cell className="w-96 flex-none">
                    <div className="flex items-start gap-3">
                      <Avatar size="small" image={vendor.avatar} square={true}>
                        {vendor.name.charAt(0)}
                      </Avatar>
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-body-bold font-body-bold text-default-font break-words">
                          {vendor.name}
                        </span>
                        {vendor.tradingName &&
                          vendor.tradingName !== vendor.name && (
                            <span className="text-caption font-caption text-neutral-500 break-words">
                              {vendor.tradingName}
                            </span>
                          )}
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="w-40 flex-none">
                    <span className="text-body font-body text-default-font">
                      {vendor.taxId}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-32 flex-none">
                    <span className="text-body font-body text-default-font">
                      {vendor.businessCategory}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-24 flex-none">
                    <Badge
                      variant={
                        vendor.status === "Verified" ? "success" : "warning"
                      }
                      icon={
                        vendor.status === "Verified" ? (
                          <FeatherCheck />
                        ) : undefined
                      }
                    >
                      {vendor.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="w-40 flex-none">
                    <div className="flex items-center gap-2">
                      <Button
                        size="small"
                        onClick={() => {
                          const selectedVendor = (vendors || []).find(
                            (v) =>
                              idsMatch(v.vendorid, vendor.id) ||
                              idsMatch(v.zoho_vendorid, vendor.zoho_vendorid),
                          );
                          if (selectedVendor) {
                            handlePartySelect(selectedVendor);
                          }
                        }}
                        variant={
                          isVendorSelected(vendor)
                            ? "brand-primary"
                            : "neutral-secondary"
                        }
                      >
                        {isVendorSelected(vendor) ? "Selected" : "Select"}
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>
          )}
        </div>
        <div className="flex w-full items-center justify-end gap-2 pt-6">
          <Button variant="neutral-tertiary" onClick={handleClose}>
            Cancel
          </Button>
          <Button disabled={!selectedParty} onClick={handleConfirm}>
            Confirm Party
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default SearchPartyModal;
